import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { Loader2, MapPin, Plus, Building2, ExternalLink } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import ImageUpload from "../../components/ImageUpload";
import { slugify } from "../../lib/slugify";

export default function VenuesTab() {
  const [venues, setVenues] = useState(null);
  const [cities, setCities] = useState([]);
  const [filterCity, setFilterCity] = useState("all");
  const [form, setForm] = useState({ name: "", city: "", address: "", capacity: "", image_url: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/venues").then(({ data }) => setVenues(data || [])).catch(() => setVenues([]));
  useEffect(() => {
    load();
    api.get("/cities").then(({ data }) => setCities((data || []).map((c) => (typeof c === "string" ? c : c.name)).filter(Boolean))).catch(() => {});
  }, []);

  const cityOf = (name) => {
    const c = (cities || []).find((x) => x.toLowerCase?.() === (name || "").toLowerCase());
    return slugify(name || "");
  };

  const shown = useMemo(() => {
    if (!venues) return [];
    const list = filterCity === "all" ? venues : venues.filter((v) => v.city === filterCity);
    return [...list].sort((a, b) => (a.city || "").localeCompare(b.city || "") || a.name.localeCompare(b.name));
  }, [venues, filterCity]);

  const addVenue = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await api.post("/business/venues", {
        name: form.name.trim(), city: form.city.trim(),
        address: form.address.trim(), capacity: parseInt(form.capacity) || 0, image_url: form.image_url || "",
      });
      toast({ title: "Venue saved", description: "Added to the shared venue list." });
      setForm({ name: "", city: "", address: "", capacity: "", image_url: "" });
      load();
    } catch {
      toast({ title: "Error", description: "Could not add venue", variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (!venues) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div className="max-w-[820px]">
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-6" data-testid="add-venue-form">
        <h3 className="font-semibold text-[#16243b] mb-1 flex items-center gap-2"><Plus size={18} className="text-[#2ec9c9]" /> Add a venue</h3>
        <p className="text-sm text-[#8a97a8] mb-4">Venues are shared across the platform — once added, any organiser can pick them for events in that city.</p>
        <form onSubmit={addVenue} className="grid sm:grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="venue-name"
            placeholder="Venue name" className="rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="venue-city"
            className="rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
            <option value="">Select city…</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="venue-address"
            placeholder="Address (optional)" className="rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          <input type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="venue-capacity"
            placeholder="Capacity (optional)" className="rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          <div className="sm:col-span-2">
            <ImageUpload label="Venue photo (optional)" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="venue-image" />
          </div>
          <div>
            <button disabled={busy} data-testid="save-venue-btn" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
              {busy ? <Loader2 className="animate-spin" size={16} /> : null} Add venue
            </button>
          </div>
        </form>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#16243b]">Shared venues ({shown.length})</h3>
        <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)} data-testid="venue-filter-city"
          className="rounded-xl border border-gray-200 px-4 py-2 text-sm outline-none focus:border-[#2ec9c9] bg-white">
          <option value="all">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        {shown.length === 0 ? (
          <p className="p-6 text-sm text-[#8a97a8]">No venues yet. Add one above to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Venue", "City", "Address", "Capacity", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {shown.map((v) => (
                <tr key={v.id} className="border-t border-gray-50" data-testid={`venue-row-${v.id}`}>
                  <td className="px-5 py-3 font-medium text-[#16243b]">
                    <span className="flex items-center gap-2">
                      {v.image_url ? <img src={v.image_url} alt={v.name} className="w-8 h-8 rounded-lg object-cover" /> : <Building2 size={14} className="text-[#2ec9c9]" />}
                      {v.name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[#5a6b80]"><span className="inline-flex items-center gap-1"><MapPin size={12} /> {v.city || "—"}</span></td>
                  <td className="px-5 py-3 text-[#5a6b80]">{v.address || "—"}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{v.capacity ? v.capacity : "—"}</td>
                  <td className="px-5 py-3 text-right">
                    {v.slug && v.city && <a href={`/venues/${slugify(v.city)}/${v.slug}`} target="_blank" rel="noreferrer" data-testid={`venue-view-${v.id}`} className="text-xs text-[#2ec9c9] hover:underline inline-flex items-center gap-1">View page <ExternalLink size={12} /></a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
